'use client'

import { useTranslations, useLocale } from 'next-intl'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { Shield, Rocket, Award, MapPin } from 'lucide-react'

export default function WhyTOBGSection() {
  const t = useTranslations('whyTobg')
  const locale = useLocale()

  const pillars = [
    { key: 'pillar1', icon: <Shield className="w-8 h-8" />, color: 'primary' },
    { key: 'pillar2', icon: <Rocket className="w-8 h-8" />, color: 'secondary' },
    { key: 'pillar3', icon: <Award className="w-8 h-8" />, color: 'primary' },
    { key: 'pillar4', icon: <MapPin className="w-8 h-8" />, color: 'secondary' },
  ]

  return (
    <section className="section-padding bg-white dark:bg-secondary-900">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-secondary-900 dark:text-white">
            {t('sectionTitle')}
          </h2>
          <p className="text-base sm:text-lg text-secondary-600 dark:text-secondary-300 max-w-3xl mx-auto">
            {t('sectionSubtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.key}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className="bg-secondary-50 dark:bg-secondary-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6 text-center"
            >
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto ${
                pillar.color === 'primary'
                  ? 'bg-primary-100 dark:bg-primary-900'
                  : 'bg-secondary-100 dark:bg-secondary-700'
              }`}>
                <div className={
                  pillar.color === 'primary'
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-secondary-600 dark:text-secondary-300'
                }>
                  {pillar.icon}
                </div>
              </div>
              <h3 className="font-bold text-secondary-900 dark:text-white mb-2">
                {t(`${pillar.key}.title`)}
              </h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                {t(`${pillar.key}.description`)}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 sm:p-12 text-white"
        >
          <h3 className="text-2xl sm:text-3xl font-bold mb-4">
            {locale === 'de' ? 'Bereit, KI in Ihr Unternehmen zu bringen?' : 'Ready to bring AI into your business?'}
          </h3>
          <p className="text-lg opacity-90 mb-8">
            {locale === 'de'
              ? 'Lassen Sie uns gemeinsam die richtige Lösung für Sie bauen.'
              : "Let's build the right solution together."}
          </p>
          <Link
            href={`/${locale}/contact`}
            className="inline-block px-8 py-4 bg-white text-primary-600 rounded-full font-bold hover:bg-secondary-50 transition-colors shadow-lg"
          >
            {locale === 'de' ? "Jetzt kontaktieren" : "Let's Talk"}
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
